/**
 * Audit Log Table Column Definitions
 *
 * Investigation-oriented columns for the audit logs BaseDataTable:
 * Time, Actor, Event (+ category chip), Scope, Target, Result, Details.
 */

import React from 'react'
import { Box, Button, Chip, Typography } from '@mui/material'
import {
  Error as ErrorIcon,
  CheckCircle as SuccessIcon,
  Warning as WarningIcon,
} from '@mui/icons-material'
import { TableColumn } from '../../../../components/shared/BaseDataTable'
import { AuditLog } from '../../../../services/auditApi'
import { formatDateTime } from '../../../../utils/inspectorFormatters'
import {
  labelFor,
  shortCategory,
  scopeFor,
  targetFor,
  resultLabel,
  severityColor,
  SEVERITY_LABELS,
} from '../auditLabels'

const resultIcon = (success: string) => {
  if (success === 'true') return <SuccessIcon color="success" fontSize="small" />
  if (success === 'partial') return <WarningIcon color="warning" fontSize="small" />
  return <ErrorIcon color="error" fontSize="small" />
}

export const createAuditLogColumns = (
  onViewDetails: (log: AuditLog) => void,
  labelMap: Map<string, string> = new Map(),
  categoryMap: Map<string, string> = new Map()
): TableColumn<AuditLog>[] => [
  {
    key: 'created_at',
    label: 'Time',
    sortable: true,
    render: (log) => (
      <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
        {formatDateTime(log.created_at)}
      </Typography>
    )
  },
  {
    key: 'user_email',
    label: 'Actor',
    render: (log) => (
      <Box>
        <Typography
          variant="body2"
          fontWeight={500}
          sx={{
            fontStyle: log.user_email ? 'normal' : 'italic',
            color: log.user_email ? 'inherit' : 'text.secondary'
          }}
        >
          {log.user_email || 'System'}
        </Typography>
        {(log.user_role || log.ip_address) && (
          <Typography variant="caption" color="text.secondary">
            {[log.user_role, log.ip_address].filter(Boolean).join(' - ')}
          </Typography>
        )}
      </Box>
    )
  },
  {
    key: 'event',
    label: 'Event',
    render: (log) => {
      const category = categoryMap.get(log.event_type) || log.event_category
      return (
        <Box>
          <Typography variant="body2" fontWeight={500}>
            {log.action || labelFor(log.event_type, labelMap)}
          </Typography>
          {category && (
            <Chip
              label={shortCategory(category)}
              size="small"
              variant="outlined"
              sx={{
                mt: 0.25,
                height: 18,
                color: 'text.secondary',
                borderColor: 'divider',
                '& .MuiChip-label': { px: 0.75, fontSize: '0.65rem' }
              }}
            />
          )}
        </Box>
      )
    }
  },
  {
    key: 'scope',
    label: 'Scope',
    render: (log) => {
      const { code, country } = scopeFor(log)
      const muted = code === 'Global' || code === 'System'
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography
            variant="body2"
            sx={{ color: muted ? 'text.secondary' : 'inherit' }}
          >
            {code}
          </Typography>
          {country && (
            <Chip
              label={country}
              size="small"
              variant="outlined"
              sx={{ height: 18, '& .MuiChip-label': { px: 0.5, fontSize: '0.6rem' } }}
            />
          )}
        </Box>
      )
    }
  },
  {
    key: 'target',
    label: 'Target',
    render: (log) => {
      const { primary, secondary } = targetFor(log)
      return (
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="body2"
            sx={{ color: primary === '-' ? 'text.secondary' : 'inherit' }}
          >
            {primary}
          </Typography>
          {secondary && (
            <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
              {secondary}
            </Typography>
          )}
        </Box>
      )
    }
  },
  {
    key: 'success',
    label: 'Result',
    render: (log) => {
      const sevColor = severityColor(log.severity)
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
          {resultIcon(log.success)}
          <Typography variant="body2">{resultLabel(log.success)}</Typography>
          {sevColor && (
            <Chip
              label={SEVERITY_LABELS[log.severity] || log.severity}
              color={sevColor}
              size="small"
              variant="outlined"
              sx={{ height: 18, '& .MuiChip-label': { px: 0.75, fontSize: '0.65rem' } }}
            />
          )}
          {log.duration_formatted && (
            <Typography variant="caption" color="text.secondary">
              {log.duration_formatted}
            </Typography>
          )}
        </Box>
      )
    }
  },
  {
    key: 'details',
    label: 'Details',
    width: 90,
    align: 'center',
    render: (log) => (
      <Button size="small" variant="text" onClick={() => onViewDetails(log)}>
        View
      </Button>
    )
  }
]

export default createAuditLogColumns
